import { AuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { ContainerDefault, ErrorMessage, ErrorMessagePage, Heading, Input } from '@core/ui';
import { useNavigate, useParams, useSearch } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecipientChunkUpload, useSpreadsheetImport } from '../../../hooks';
import { SpreadsheetImport } from '../../organism';
import { emailListImportFields } from './emailListImportFields';

export const ImportEmailListPage = () => {
    const canManageEmailLists = useContext(AuthContext).canManageEmailLists;
    const { t } = useTranslation();
    const nav = useNavigate();
    const isHydrated = useIsHydrated();
    // webinarId set only under /$webinarId/marketing/import. campaignId is an optional search param, set
    // when reached from a campaign's Audience tab — used to navigate back to that campaign once import finishes.
    const { webinarId } = useParams({ strict: false });
    const { campaignId } = useSearch({ strict: false });

    const [description, setDescription] = useState('');
    const [descriptionError, setDescriptionError] = useState<string>();

    const importer = useSpreadsheetImport(emailListImportFields);
    const upload = useRecipientChunkUpload();

    const createMutation = useMutation(OptionsApi.emailLists.create());
    const finalizeMutation = useMutation(OptionsApi.emailLists.finalize());

    const error = createMutation.error ?? upload.error ?? finalizeMutation.error ?? null;

    // Create the list, stream recipients up in chunks, then finalize and open the detail page.
    const startImport = () => {
        if (!description.trim()) {
            setDescriptionError(t('EmailLists.Import.Error.DescriptionRequired'));
            return;
        }
        setDescriptionError(undefined);

        const recipients = importer.buildProfiles();
        if (!recipients || recipients.length === 0) {
            return;
        }

        createMutation.mutate(
            { description: description.trim(), webinarId },
            {
                onSuccess: (id) => {
                    upload.start(id, recipients, {
                        onComplete: () =>
                            finalizeMutation.mutate(id, {
                                // Webinar mode: back to the originating campaign if there is one, else the campaign overview.
                                onSuccess: () => {
                                    if (webinarId && campaignId) nav({ to: '/$webinarId/marketing/$id', params: { webinarId, id: campaignId } });
                                    else if (webinarId) nav({ to: '/$webinarId/marketing', params: { webinarId } });
                                    else nav({ to: '/manage/email-lists/$id', params: { id } });
                                }
                            })
                    });
                }
            }
        );
    };

    if (!canManageEmailLists) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const isImporting = createMutation.isPending || upload.isUploading || finalizeMutation.isPending;

    return (
        <ContainerDefault>
            {webinarId && campaignId ? (
                <BackLink to="/$webinarId/marketing/$id" params={{ webinarId, id: campaignId }}>
                    {t('EmailLists.Button.BackToCampaign')}
                </BackLink>
            ) : webinarId ? (
                <BackLink to="/$webinarId/marketing" params={{ webinarId }}>
                    {t('EmailLists.Button.BackToLists')}
                </BackLink>
            ) : (
                <BackLink to="/manage/email-lists">{t('EmailLists.Button.BackToLists')}</BackLink>
            )}
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('EmailLists.Import.PageHeading')}
            </Heading>

            {error != null && <ErrorMessage apiError={error} classNames="mb-4 max-w-4xl" />}

            <div className="max-w-lg mb-8">
                <Input
                    data-testid="email-list-description"
                    label={t('EmailLists.Import.Label.Description')}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    errorMessage={descriptionError}
                />
            </div>

            <SpreadsheetImport
                importer={importer}
                isImporting={isImporting}
                onImport={isHydrated ? startImport : () => {}}
                importButtonText={t('EmailLists.Import.Button.Import')}
                importTestId="email-list-import-submit"
                progressOpen={upload.isUploading || finalizeMutation.isPending}
                progressCurrent={upload.progressCurrent}
                progressTotal={upload.progressTotal}
            />
        </ContainerDefault>
    );
};
